import { useEffect, useMemo, useState } from "react";
import { getApiErrorMessage } from "../../lib";
import { getKybStats, GetKybStatsParams } from "../kyb";
import { KybStatsSummary } from "../../../features/kyb-compliance/types";

type UseKybStatsState = {
  stats: KybStatsSummary | null;
  isLoading: boolean;
  error: string | null;
};

const kybStatsCache = new Map<string, KybStatsSummary>();

export const useKybStats = (params: GetKybStatsParams) => {
  const [state, setState] = useState<UseKybStatsState>({
    stats: null,
    isLoading: true,
    error: null,
  });
  const [refreshCount, setRefreshCount] = useState(0);
  const requestKey = JSON.stringify(params);
  const requestParams = useMemo(
    () => JSON.parse(requestKey) as GetKybStatsParams,
    [requestKey],
  );

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const cached = kybStatsCache.get(requestKey);
      setState((prev) => ({
        stats: cached ?? prev.stats,
        isLoading: cached ? false : true,
        error: null,
      }));

      try {
        const stats = await getKybStats(requestParams);
        if (!isMounted) return;
        kybStatsCache.set(requestKey, stats);
        setState({
          stats,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        if (!isMounted) return;
        setState({
          stats: null,
          isLoading: false,
          error: getApiErrorMessage(error, "Unable to load KYB stats"),
        });
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [refreshCount, requestKey, requestParams]);

  return {
    ...state,
    refetch: () => setRefreshCount((prev) => prev + 1),
  };
};
